import { Grid, GridColumn, IFormHelperProps, Notice, Select, Spinner, Text, TextField } from '@teliaee/design-system';
import { observer } from 'mobx-react';
import * as React from 'react';
import LoginButton from '../../components/LoginButton';
import PasskeyOfferRegister from '../../components/PasskeyOfferRegister';
import { CountryCode } from '../../stores/SmartIdLoginStore';
import { PERSONAL_CODE_FIELD_ID, getHelperErrorMessage, helperInErrorState, personalCodeHelper, validatePersonalCode } from '../../util/FieldHelperUtil';
import { runInTeliaRumService } from '../../util/TeliaOtelUtil';
import RememberMe from '../RememberMe';
import PendingLogin from './PendingLogin';
import PollableLoginStatusNotice from './PollableLoginStatusNotice';
import ServiceStatusNotice from './ServiceStatusNotice';
import useStores from '../../stores/UseStores';
import { TestAuthWarning } from './TestAuthWarning';

const SmartIdLogin = observer((): React.JSX.Element => {
  const {
    smartIdLoginStore,
    translateStore: { translate },
    uiStore: { getLoginPanelGridWidth, showErrorNoticeWithErroredFields, setSelectedTab },
    passkeyStore,
  } = useStores();
  const { loginCredentials, status } = smartIdLoginStore;
  const [hasSubmitted, setHasSubmitted] = React.useState<boolean>(false);
  const [perCodeHelper, setPerCodeHelper] = React.useState<IFormHelperProps | undefined>(undefined);

  const getPersonalCodeHelper = (forceIsDirty?: boolean) =>
    personalCodeHelper(translate, loginCredentials.personalCode, loginCredentials.countryCode, forceIsDirty ?? hasSubmitted);

  React.useEffect(() => {
    setPerCodeHelper(getPersonalCodeHelper());
  }, [loginCredentials.personalCode, hasSubmitted]);

  React.useEffect(() => {
    smartIdLoginStore.fetchServiceStatus();
  }, []);

  const isInvalid = () => {
    return (
      !loginCredentials.personalCode ||
      (loginCredentials.countryCode === 'EE' && !validatePersonalCode(loginCredentials.personalCode)) ||
      !loginCredentials.countryCode
    );
  };

  const login = (event: React.FormEvent) => {
    runInTeliaRumService('Autentimise algatamine (SID)', () => {
      event.preventDefault();
      setHasSubmitted(true);
      if (!isInvalid()) {
        smartIdLoginStore.startLogin();
      } else {
        showErrorNoticeWithErroredFields('form.error', [getPersonalCodeHelper(true)]);
      }
    });
  };

  if (passkeyStore.offerRegisterModalVisible) {
    return <PasskeyOfferRegister />;
  }

  if (status === 'REDIRECTING' || status === 'SUCCESS') {
    return <Spinner />;
  }

  return (
    <>
      <span className="sr-only" aria-live="polite">
        {status === 'PENDING' && `${translate('sid.instruction')} ${smartIdLoginStore.verificationCode?.split('').join(' ')}`}
      </span>
      {status === 'PENDING' ? (
        <PendingLogin method={'sid'} pollableLoginStore={smartIdLoginStore} />
      ) : (
        <>
          <ServiceStatusNotice pollableLoginStore={smartIdLoginStore} />
          <form onSubmit={(event) => login(event)} id="smartIdLogin">
            <Text>
              <PollableLoginStatusNotice pollableLoginStore={smartIdLoginStore} />
              <Grid align={['center-sm']}>
                <GridColumn width={['sm-10', 'md-8', 'lg-8']}>
                  <TestAuthWarning method="sid" />
                </GridColumn>
              </Grid>
              <Grid align={['center-sm']}>
                {smartIdLoginStore.shouldSavePasskeyAfterLogin && (
                  <GridColumn width={getLoginPanelGridWidth()}>
                    <Notice type="info">{translate('passkey.save.after.login.notice')}</Notice>
                  </GridColumn>
                )}
                <GridColumn width={getLoginPanelGridWidth()}>
                  <Text>
                    <TextField
                      label={translate('panel.smartId.label.personalCode')}
                      id={PERSONAL_CODE_FIELD_ID}
                      value={loginCredentials.personalCode}
                      onChange={(value) => (loginCredentials.personalCode = value)}
                      isInvalid={helperInErrorState(perCodeHelper)}
                      input={{ inputMode: 'numeric' }}
                      errorMessage={getHelperErrorMessage(perCodeHelper)}
                      required={true}
                    />
                    <Select
                      label={translate('panel.smartId.label.countryCode')}
                      id="smart-id-country"
                      value={loginCredentials.countryCode}
                      simpleValue={true}
                      onChange={(value) => (loginCredentials.countryCode = value as string)}
                      options={Object.values(CountryCode).map((countryCode) => ({
                        label: translate(`panel.smartId.label.countryCode.${countryCode}`),
                        value: countryCode,
                      }))}
                      required={true}
                    />
                    <RememberMe tabItemId="smartIdLoginTab" />
                    <LoginButton isLoading={status === 'SUBMITTING'} disabled={status === 'SUBMITTING'} onClick={() => setSelectedTab('smartId')} />
                  </Text>
                </GridColumn>
              </Grid>
            </Text>
          </form>
        </>
      )}
    </>
  );
});

export default SmartIdLogin;
